import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';

import {
  ManageIntegrationDataPane,
  ManageIntegrationDataStep,
} from '../ManageIntegrationDataModal.types';
import { CashflowsImportForm } from '../steps/importData/hooks/useCashflowsImportForm';
import { EntitiesImportForm } from '../steps/importData/hooks/useEntitiesImportForm';
import { LiabilitiesImportForm } from '../steps/importData/hooks/useLiabilitiesImportForm';
import { LifeInsuranceImportForm } from '../steps/importData/hooks/useLifeInsuranceImportForm';
import { EntityLinkForm } from '../steps/linkData/hooks/useEntityLinkForm';
import { SelectClientsForm } from '../steps/selectClients/useSelectClientsForm';

export interface ManageIntegrationDataContextShape {
  /** The household this flow is editing. */
  householdId: string;

  /** The pane on screen, and the step that owns it. */
  activePane: ManageIntegrationDataPane;
  currentStep: ManageIntegrationDataStep;
  activeStepIndex: number;
  isFirstStep: boolean;
  isLastStep: boolean;

  /** True while a step is committing; panes disable their inputs. */
  isSaving: boolean;

  /** Navigate to the step (and tab) that owns a pane. */
  goToPane: (pane: ManageIntegrationDataPane) => void;
  goToStepIndex: (index: number) => void;
  goToPreviousStep: () => void;
  goToNextStep: () => void;

  /** Commit the step on screen, then close. */
  commitAndClose: () => void;
  /** Close without committing, for panes that hand the user off elsewhere. */
  closeModal: () => void;

  selectClientsForm: SelectClientsForm;
  directHoldingsLinkForm: EntityLinkForm;
  trustsBusinessesLinkForm: EntityLinkForm;
  entitiesImportForm: EntitiesImportForm;
  liabilitiesImportForm: LiabilitiesImportForm;
  lifeInsuranceImportForm: LifeInsuranceImportForm;
  cashflowsImportForm: CashflowsImportForm;
}

export const ManageIntegrationDataContext =
  createContext<ManageIntegrationDataContextShape | null>(null);

export const useManageIntegrationDataContext = () =>
  useGuardedContext(
    ManageIntegrationDataContext,
    'ManageIntegrationDataProvider'
  );
